import * as React from "react";
import {observer} from "mobx-react";
import {Table, message, Button} from "antd";
import {injectIntl, InjectedIntlProps} from "react-intl";
import empty from "../../../assets/image/empty.svg";
import CsvDownloader from "react-csv-downloader";
import moment from "moment";
import {consoleApi} from "../../../apiMiddleware";
import {IReactionDisposer, reaction} from "mobx";
import {logStore} from "../../../models/LogStore";
import {LoadingOutlined} from "@ant-design/icons/lib";
import {Chart, LineAdvance} from "bizcharts";
import copy from "copy-to-clipboard";

export type RoomMinDosageType = {
    segment: string,
    writersCount: string,
    readonlyCount: string,
    sessionsCount: string,
    writersPeakCount: string,
    readonlyPeakCount: string,
    sessionsPeakCount: string,
};

export type TimeDetailProps = {
    uuid: string,
    startTime: string,
} & InjectedIntlProps;

export type TimeDetailState = {
    roomDosageArray: any[],
    roomDosageTimeArray: any[],
    isMaskLoading: boolean,
};

@observer
class TimeDetail extends React.Component<TimeDetailProps, TimeDetailState> {
    private readonly reactionDateDisposer: IReactionDisposer;
    public constructor(props: TimeDetailProps) {
        super(props);
        this.reactionDateDisposer = this.startDateReaction();
        this.state = {
            roomDosageArray: [],
            roomDosageTimeArray: [],
            isMaskLoading: false,
        };
    }
    private startDateReaction(): IReactionDisposer {
        return reaction(() => {
            return logStore.selectedDate;
        }, async date => {
            await this.handleSelectChange(date);
        });
    }
    public async componentDidMount(): Promise<void> {
        const {uuid, startTime} = this.props;
        try {
            this.setState({isMaskLoading: true});
            await this.handleAllRoom(uuid);
            const start = moment(parseInt(startTime)).startOf("day").valueOf();
            const end = moment(parseInt(startTime)).endOf("day").valueOf();
            const resp = await consoleApi.data.getRoomDataMinuteDetail(uuid, start, end);
            if (resp.code === 200) {
                const roomDosageArray = resp.data.map((data: any, index: number) => {
                    return {
                        key: `data-${index}`,
                        segment: moment(data.segment).format("LT"),
                        writersCount: data.writersCount,
                        readonlyCount: data.readonlyCount,
                        sessionsCount: data.sessionsCount,
                        writersPeakCount: data.writersPeakCount,
                        readonlyPeakCount: data.readonlyPeakCount,
                        sessionsPeakCount: data.sessionsPeakCount,
                    };
                });
                this.setState({roomDosageArray: roomDosageArray, isMaskLoading: false});
            }
        } catch (err) {
            message.error(`${err}`);
            this.setState({isMaskLoading: false});
        }
    }

    private renderTable(roomDosageArray: RoomMinDosageType[]): React.ReactNode {
        const { intl } = this.props;
        const {uuid} = this.props;
        if (roomDosageArray && roomDosageArray.length > 0) {
            const headers = [
                {displayName: intl.formatMessage({id: "time"}), id: "segment"},
                {displayName: intl.formatMessage({id: "interactive"}), id: "writersCount"},
                {displayName: intl.formatMessage({id: "readonly"}), id: "readonlyCount"},
                {displayName: intl.formatMessage({id: "duration"}), id: "sessionsCount"},
                {displayName: intl.formatMessage({id: "interactivePeak"}), id: "writersPeakCount"},
                {displayName: intl.formatMessage({id: "readonlyPeak"}), id: "readonlyPeakCount"},
                {displayName: intl.formatMessage({id: "durationPeak"}), id: "sessionsPeakCount"},
            ];
            const columns = [
                {
                    title: intl.formatMessage({id: "time"}),
                    dataIndex: "segment",
                    key: "segment",
                },
                {
                    title: intl.formatMessage({id: "interactive"}),
                    dataIndex: "writersCount",
                    key: "writersCount",
                },
                {
                    title: intl.formatMessage({id: "readonly"}),
                    dataIndex: "readonlyCount",
                    key: "readonlyCount",
                },
                {
                    title: intl.formatMessage({id: "duration"}),
                    dataIndex: "sessionsCount",
                    key: "sessionsCount",
                },
                {
                    title: intl.formatMessage({id: "interactive-peak"}),
                    dataIndex: "writersPeakCount",
                    key: "writersPeakCount",
                },
                {
                    title: intl.formatMessage({id: "readonly-peak"}),
                    dataIndex: "readonlyPeakCount",
                    key: "readonlyPeakCount",
                },
                {
                    title: intl.formatMessage({id: "duration-peak"}),
                    dataIndex: "sessionsPeakCount",
                    key: "sessionsPeakCount",
                },
            ];
            let writableDataChartSource: any[] = [];
            if (roomDosageArray.length > 0) {
                writableDataChartSource = roomDosageArray.map((data, index) => {
                    return {
                        key: `data0-${index}`,
                        segment: data.segment,
                        duration: data.writersCount,
                        type: "可写时长",
                    };
                });
            }
            let readonlyDataChartSource: any[] = [];
            if (roomDosageArray.length > 0) {
                readonlyDataChartSource = roomDosageArray.map((data, index) => {
                    return {
                        key: `data1-${index}`,
                        segment: data.segment,
                        duration: data.readonlyCount,
                        type: "只读时长",
                    };
                });
            }
            const dataChartSource = [...writableDataChartSource, ...readonlyDataChartSource];
            return (
                <>
                    <Chart
                        autoFit
                        padding={[20, 120, 96, 120]}
                        height={300}
                        width={1200}
                        data={dataChartSource}>
                        <LineAdvance
                            shape="smooth"
                            point
                            area
                            position="segment*duration"
                            color={["type", ["#3381FF", "#3DC797"]]}
                        />
                    </Chart>
                    <Table bordered title={() => {
                        return (
                            <div className="product-center-chart-title">
                                <div
                                    className="product-center-chart-uuid"
                                    onClick={() => {
                                        copy(uuid);
                                        message.success("Copy to clipboard");
                                    }}>
                                    {uuid}
                                </div>
                                <CsvDownloader suffix={false} bom={true} prefix={false} filename={"text"} columns={headers}
                                               datas={roomDosageArray}>
                                    <Button type="primary" size={"small"}>
                                        Download
                                    </Button>
                                </CsvDownloader>
                            </div>
                        );
                    }} style={{width: "100%", marginTop: 64}} columns={columns} dataSource={roomDosageArray}/>
                </>
            );
        } else {
            return (
                <div className={"table-empty-box"}>
                    <img src={empty} alt={"empty"}/>
                </div>
            );
        }
    }

    private handleAllRoom = async (value: string): Promise<void> => {
        try {
            const resp = await consoleApi.data.getRoomDataDetail(value);
            console.log(resp);
            if (resp.code === 200 && resp.data.length > 0) {
                this.setState({roomDosageTimeArray: resp.data});
            } else {
                message.info("No room information");
            }
        } catch (err) {
            message.error(`${err}`);
        }
    }

    private handleSelectChange = async (value: number): Promise<void> => {
        const start = moment(value).startOf("day").valueOf();
        const end = moment(value).endOf("day").valueOf();
        try {
            this.setState({isMaskLoading: true});
            const resp = await consoleApi.data.getRoomDataMinuteDetail(this.props.uuid, start, end);
            if (resp.code === 200) {
                const roomDosageArray = resp.data.map((data: any, index: number) => {
                    return {
                        key: `data-${index}`,
                        segment: moment(data.segment).format("LT"),
                        writersCount: data.writersCount,
                        readonlyCount: data.readonlyCount,
                        sessionsCount: data.sessionsCount,
                        writersPeakCount: data.writersPeakCount,
                        readonlyPeakCount: data.readonlyPeakCount,
                        sessionsPeakCount: data.sessionsPeakCount,
                    };
                });
                this.setState({roomDosageArray: roomDosageArray, isMaskLoading: false});
            }
        } catch (err) {
            message.error(`${err}`);
            this.setState({isMaskLoading: false});
        }
    }
    private renderMask = (): React.ReactNode => {
        if (this.state.isMaskLoading) {
            return (
                <div className="content-box-mask">
                    <LoadingOutlined style={{fontSize: 36, color: "#3381FF"}} />
                </div>
            );
        } else {
            return null;
        }
    }
    public render(): React.ReactNode {
        const {roomDosageArray} = this.state;
        return (
            <div className="dash-out-box">
                <div className="dash-mid-box">
                    <div className="dash-content">
                        <div className="portal-room-chart">
                            <div className="portal-room-box-mid">
                                {this.renderMask()}
                                <div className="console-content-chart-body">
                                    {this.renderTable(roomDosageArray)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }


}

export default injectIntl(TimeDetail);



// WEBPACK FOOTER //
// ./src/components/portal/logSearch/TimeDetail.tsx